// competitionHints.js — advisory-only roster-competition hints for the
// Projection Lab wizard (rookie/vet arrivals crowding an incumbent's touches).
//
// Vault-validated against real 2025 outcomes but fails leave-one-out MAE as
// a predictor on its own - these are surfaced to the user as a nudge, NEVER
// auto-applied to allocs. computeCompetitionHints never mutates anything;
// it's a pure read of teamStarters + DRAFT_CAPITAL_2026 + PLAYER_HISTORY.

import { TEAM_HISTORY as PLAY_HISTORY } from "../data/projectionData";
import { DRAFT_CAPITAL_2026 } from "../data/draftCapital2026";
import { findRbContract } from "../data/rbContracts";
import { findPlayerHist } from "./projlabProjection";

const WR_SLOTS = ["WR1", "WR2", "WR3"];
const RB_SLOTS = ["RB1", "RB2"];

// Sleeper-sourced rosters sometimes carry JAC/LAR; this app's own data
// (PLAYER_HISTORY, TEAM_HISTORY) uses JAX/LA - normalize defensively on the
// old-team lookup even though current data hasn't been observed to need it.
const TEAM_NORM = { JAC: "JAX", LAR: "LA" };
const normTeam = (t) => TEAM_NORM[t] ?? t;

// Same suffix-strip regex as findPlayerHist (projlabProjection.js).
function stripSuffix(name) {
  return name.replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i, "").trim();
}

function draftPick(name) {
  return DRAFT_CAPITAL_2026[stripSuffix(name)]?.pick ?? null;
}

// This player's own most recent NFL season (year === 2025), or null if they
// have no PLAYER_HISTORY record at all (true rookies, fail-safe).
function get2025Record(name, sleeperId) {
  const hist = findPlayerHist(name, sleeperId);
  return hist?.find(h => h.year === 2025) ?? null;
}

function slotPlayers(teamStarters, slotKeys) {
  return slotKeys
    .map(key => {
      const slot = teamStarters?.[key];
      if (!slot?.player) return null;
      const pick = draftPick(slot.player);
      return { key, player: slot.player, sleeperId: slot.sleeper_id, isRookie: pick != null, pick };
    })
    .filter(Boolean);
}

// "Highest" = lowest slot number (WR1 > WR2 > WR3, RB1 > RB2).
function highestBySlotOrder(candidates, slotOrder) {
  if (!candidates.length) return null;
  return [...candidates].sort((a, b) => slotOrder.indexOf(a.key) - slotOrder.indexOf(b.key))[0];
}

// Arriving-vet share check, shared by the WR (target share) and RB (rush
// share) rules - both compare a stat off the player's 2025 team's team-level
// total for that stat. Returns null on any missing data (fail-safe).
function oldTeamShare(rec, abbrev, statField, teamTotalField) {
  if (!rec) return null;
  const oldTeam = normTeam(rec.team);
  // Normalize BOTH sides - a JAX player whose 2025 record happens to say
  // "JAC" (or LA/"LAR") must not read as a team change against his own
  // current team just because the raw strings differ.
  if (oldTeam === normTeam(abbrev)) return null; // didn't change teams
  const oldPlay = (PLAY_HISTORY[oldTeam] || []).find(y => y.year === 2025);
  if (!oldPlay || !oldPlay[teamTotalField]) return null;
  return (rec[statField] || 0) / oldPlay[teamTotalField];
}

// The TARGET incumbent's own trailing rush share on his CURRENT team - the
// mirror of oldTeamShare (team === abbrev instead of !==). Null on any
// missing data or team mismatch (fail-safe: caller keeps current behavior).
function ownTeamRushShare(rec, abbrev) {
  if (!rec || normTeam(rec.team) !== normTeam(abbrev)) return null;
  const play = (PLAY_HISTORY[normTeam(abbrev)] || []).find(y => y.year === 2025);
  if (!play || !play.rushAtt) return null;
  return (rec.rushAtt || 0) / play.rushAtt;
}

/**
 * computeCompetitionHints(abbrev, teamStarters) -> { [slotKey]: hint }
 * hint = { level: "warn"|"info", kind, suggest, rival, pick }
 * pick is the triggering player's draft pick when they're a 2026 rookie,
 * else null (an arriving veteran has no draft pick to cite).
 *
 * Rules (WR1-3 / RB1-2 only - TE has no rule, "untestable"):
 * 1. Rookie WR pick <=32 in the room -> warn on the highest non-rookie WR
 *    slot (excluding the rookie's own slot), kind "r1-wr".
 * 2. Only when no R1 rookie WR is present: rookie WR pick 33+, OR an
 *    arriving vet WR (2025 team changed AND old-team target share >=15%)
 *    -> info on that SAME slot, kind "wr-null", suggest null.
 * 3. Rookie RB pick <=32 -> warn on the other non-rookie RB slot, kind
 *    "r1-rb-viability", suggest null (no pp estimate - too volatile).
 * 4. Rookie RB pick 33-100, OR an arriving vet RB (2025 team changed AND
 *    old-team rush share >=25%) -> warn on the OTHER non-rookie RB slot;
 *    suggest text depends on which slot receives the warn (RB1 vs RB2).
 *    Day-3 rookie RBs (pick > 100) get nothing under any rule.
 *
 * Vet-arrival qualifier (v2, vault-validated 2026-07-16,
 * incumbent_vet_quality_split.py addendum in Incumbent_Competition_Discount):
 * the vet-arrival share hit is a COMMITTEE phenomenon - bellcow incumbents
 * (trailing rush share >=45%) show -4.2pp CI[-10.9,+2.3] (not significant)
 * vs committee (25-45%) -11.9pp CI[-19.5,-3.6]. The vet's trailing share
 * does NOT scale the hit (r²=0.002); his contract directionally does
 * (>=~2% of cap: -12.9pp n=5 fragile; committee × paid vet: -16.6pp
 * CI[-27.6,-7.0]). So rule 4's VET trigger is tiered by the incumbent's own
 * trailing rush share × the vet's apyM (RB_CONTRACTS):
 *   - incumbent >=45% + vet apyM < 3  -> info "rb-vet-null" (no discount)
 *   - incumbent >=45% + vet apyM >= 6 -> warn, trim ~3-5pp (thin, n=3)
 *   - incumbent <45%  + vet apyM >= 6 -> warn, trim ~10-15pp (the sig cell)
 *   - anything else / unresolvable    -> existing v1 behavior unchanged
 * Rookie triggers are untouched.
 */
export function computeCompetitionHints(abbrev, teamStarters) {
  const hints = {};

  const wrInfo = slotPlayers(teamStarters, WR_SLOTS);
  const rbInfo = slotPlayers(teamStarters, RB_SLOTS);

  // ── WR rules ────────────────────────────────────────────────────────────
  const r1RookieWRs = wrInfo.filter(w => w.isRookie && w.pick <= 32);

  if (r1RookieWRs.length) {
    for (const rookie of r1RookieWRs) {
      const nonRookies = wrInfo.filter(w => w.key !== rookie.key && !w.isRookie);
      const target = highestBySlotOrder(nonRookies, WR_SLOTS);
      if (!target) continue;
      hints[target.key] = { level: "warn", kind: "r1-wr", suggest: "trim ~3-4pp tgt", rival: rookie.player, pick: rookie.pick };
    }
  } else {
    for (const w of wrInfo) {
      let flagged = false;
      if (w.isRookie) {
        flagged = w.pick >= 33;
      } else {
        const rec = get2025Record(w.player, w.sleeperId);
        const tgtShare = oldTeamShare(rec, abbrev, "targets", "passAtt");
        flagged = tgtShare != null && tgtShare >= 0.15;
      }
      if (flagged) hints[w.key] = { level: "info", kind: "wr-null", suggest: null, rival: w.player, pick: w.pick ?? null };
    }
  }

  // ── RB rules ────────────────────────────────────────────────────────────
  const r1RookieRBs = rbInfo.filter(r => r.isRookie && r.pick <= 32);
  for (const rookie of r1RookieRBs) {
    const nonRookies = rbInfo.filter(r => r.key !== rookie.key && !r.isRookie);
    const target = highestBySlotOrder(nonRookies, RB_SLOTS);
    if (!target) continue;
    hints[target.key] = { level: "warn", kind: "r1-rb-viability", suggest: null, rival: rookie.player, pick: rookie.pick };
  }

  for (const r of rbInfo) {
    let trigger = null;
    let triggerPick = null;
    let triggerIsVet = false;
    if (r.isRookie) {
      if (r.pick >= 33 && r.pick <= 100) { trigger = r.player; triggerPick = r.pick; } // Day 2 only; Day 3 (>100) gets nothing
    } else {
      const rec = get2025Record(r.player, r.sleeperId);
      const rushShare = oldTeamShare(rec, abbrev, "rushAtt", "rushAtt");
      if (rushShare != null && rushShare >= 0.25) { trigger = r.player; triggerIsVet = true; }
    }
    if (!trigger) continue;

    // Missing contract -> unknown -> v1 behavior (see qualifier doc above).
    const vetApyM = triggerIsVet ? (findRbContract(trigger)?.apyM ?? null) : null;

    const others = rbInfo.filter(x => x.key !== r.key && !x.isRookie);
    for (const target of others) {
      if (hints[target.key]) continue; // don't clobber an existing r1-rb-viability warn
      // "rb-touches" isn't a label the spec named explicitly for this rule -
      // chosen to read clearly alongside "r1-rb-viability".
      const suggest = target.key === "RB1" ? "trim ~3-5pp rush" : "trim ~8-12pp rush";
      let hint = { level: "warn", kind: "rb-touches", suggest, rival: trigger, pick: triggerPick };

      if (vetApyM != null) {
        const incShare = ownTeamRushShare(get2025Record(target.player, target.sleeperId), abbrev);
        if (incShare != null) {
          if (incShare >= 0.45 && vetApyM < 3) {
            hint = { level: "info", kind: "rb-vet-null", suggest: null, rival: trigger, pick: null, apyM: vetApyM };
          } else if (incShare >= 0.45 && vetApyM >= 6) {
            hint = { level: "warn", kind: "rb-touches", suggest: "trim ~3-5pp rush", rival: trigger, pick: null, apyM: vetApyM, vetTier: "bellcow-paid" };
          } else if (incShare < 0.45 && vetApyM >= 6) {
            hint = { level: "warn", kind: "rb-touches", suggest: "trim ~10-15pp rush", rival: trigger, pick: null, apyM: vetApyM, vetTier: "committee-paid" };
          }
          // Bellcow + mid-tier vet ($3-6M), or committee + sub-$6M vet:
          // fall through to v1 behavior.
        }
      }
      hints[target.key] = hint;
    }
  }

  return hints;
}
